import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Trash2, Shield, CheckCircle, Eye, Image, AlertTriangle, BarChart3, Settings, Users, Search, ArrowUpDown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { CardGuardUpload } from '../../components/CardGuardUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Sub-components ----

function StatsCard({ label, value, color = 'text-primary', icon: Icon }) {
  return (
    <div className="bg-card border border-border/50 rounded-xl p-4" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground/50" />}
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function StatsTab({ stats, t }) {
  if (!stats) return <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>;
  return (
    <div className="space-y-6" data-testid="aml-stats-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard label={t('aml_stats_total')} value={stats.total_flags} icon={Shield} />
        <StatsCard label={t('aml_stats_open')} value={stats.open_flags} color="text-red-600" icon={AlertTriangle} />
        <StatsCard label={t('aml_stats_investigating')} value={stats.investigating_flags} color="text-blue-600" icon={Search} />
        <StatsCard label={t('aml_stats_resolved')} value={stats.resolved_flags} color="text-emerald-600" icon={CheckCircle} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard label={t('aml_stats_high_risk')} value={stats.high_risk_active} color="text-red-600" />
        <StatsCard label={t('aml_stats_medium_risk')} value={stats.medium_risk_active} color="text-amber-600" />
        <StatsCard label={t('aml_stats_low_risk')} value={stats.low_risk_active} color="text-blue-600" />
        <StatsCard label={t('aml_stats_false_positive')} value={stats.false_positive_flags} color="text-gray-500" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard label={t('aml_stats_today')} value={stats.today_flags} />
        <StatsCard label={t('aml_stats_week')} value={stats.week_flags} />
        <StatsCard label={t('aml_stats_month')} value={stats.month_flags} />
        <StatsCard label={t('aml_stats_held')} value={stats.held_transfers} color="text-orange-600" />
      </div>
      <StatsCard label={t('aml_stats_risky_users')} value={stats.high_risk_users} color="text-red-600" icon={Users} />
      {stats.flag_type_breakdown && Object.keys(stats.flag_type_breakdown).length > 0 && (
        <div className="bg-card border border-border/50 rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-3">{t('aml_flag_type_labels')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(stats.flag_type_breakdown).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between bg-secondary/30 rounded-lg px-3 py-2">
                <span className="text-xs text-muted-foreground">{t(`aml_flag_${type}`) || type}</span>
                <span className="text-sm font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsTab({ t }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getAdminAMLSettings().then(r => setSettings(r.data)).catch(() => toast.error('Failed to load AML settings'));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateAdminAMLSettings(settings);
      toast.success(t('aml_settings_saved'));
    } catch {
      toast.error(t('aml_settings_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (!settings) return <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>;

  const fields = [
    { key: 'high_amount_threshold', label: t('aml_settings_high_amount'), type: 'number' },
    { key: 'medium_amount_threshold', label: t('aml_settings_medium_amount'), type: 'number' },
    { key: 'daily_cumulative_limit', label: t('aml_settings_daily_limit'), type: 'number' },
    { key: 'weekly_cumulative_limit', label: t('aml_settings_weekly_limit'), type: 'number' },
    { key: 'hourly_frequency_limit', label: t('aml_settings_hourly_freq'), type: 'number' },
    { key: 'same_recipient_daily_limit', label: t('aml_settings_same_recipient'), type: 'number' },
    { key: 'new_user_high_amount', label: t('aml_settings_new_user_amount'), type: 'number' },
    { key: 'new_user_days', label: t('aml_settings_new_user_days'), type: 'number' },
    { key: 'night_hours_start', label: t('aml_settings_night_start'), type: 'number' },
    { key: 'night_hours_end', label: t('aml_settings_night_end'), type: 'number' },
    { key: 'night_transfer_threshold', label: t('aml_settings_night_threshold'), type: 'number' },
    { key: 'round_amount_threshold', label: t('aml_settings_round_threshold'), type: 'number' },
  ];

  return (
    <div className="space-y-6" data-testid="aml-settings-tab">
      <div className="bg-card border border-border/50 rounded-xl p-6">
        <h3 className="font-heading text-lg font-semibold mb-1">{t('aml_settings_title')}</h3>
        <p className="text-sm text-muted-foreground mb-6">{t('aml_settings_desc')}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fields.map(f => (
            <div key={f.key} className="space-y-1.5">
              <Label className="text-xs">{f.label}</Label>
              <Input
                type="number"
                value={settings[f.key] ?? ''}
                onChange={e => updateField(f.key, parseFloat(e.target.value) || 0)}
                data-testid={`aml-setting-${f.key}`}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-border">
          <Switch
            checked={settings.auto_hold_high_risk ?? true}
            onCheckedChange={v => updateField('auto_hold_high_risk', v)}
            data-testid="aml-setting-auto-hold"
          />
          <Label>{t('aml_settings_auto_hold')}</Label>
        </div>
        <Button className="mt-6" onClick={handleSave} disabled={saving} data-testid="save-aml-settings">
          {saving ? t('processing') : t('save')}
        </Button>
      </div>
    </div>
  );
}

function RiskUsersTab({ t }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getAdminHighRiskUsers().then(r => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const getTierColor = (tier) => {
    const colors = {
      critical: 'bg-red-600 text-white',
      high: 'bg-red-500/15 text-red-700 border border-red-500/20',
      medium: 'bg-amber-500/15 text-amber-700 border border-amber-500/20',
      low: 'bg-blue-500/15 text-blue-700 border border-blue-500/20',
    };
    return colors[tier] || colors.low;
  };

  const getTierLabel = (tier) => {
    const labels = { critical: t('aml_risk_critical'), high: t('admin_aml_risk_high'), medium: t('admin_aml_risk_medium'), low: t('admin_aml_risk_low') };
    return labels[tier] || tier;
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>;

  return (
    <div data-testid="aml-risk-users-tab">
      {users.length === 0 ? (
        <div className="text-center py-20">
          <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">{t('admin_data_not_found')}</p>
        </div>
      ) : (
        <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="text-left px-4 py-3 font-medium">{t('aml_user_name')}</th>
                <th className="text-left px-4 py-3 font-medium">{t('aml_user_email')}</th>
                <th className="text-center px-4 py-3 font-medium">{t('aml_user_risk_score')}</th>
                <th className="text-center px-4 py-3 font-medium">{t('aml_user_risk_tier')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className="border-b border-border/50 hover:bg-secondary/20" data-testid={`risk-user-${user.id}`}>
                  <td className="px-4 py-3 font-medium">{user.full_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-bold text-lg">{user.risk_score}</span>
                    <span className="text-muted-foreground text-xs">/100</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierColor(user.risk_tier)}`}>
                      {getTierLabel(user.risk_tier)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ---- Main Page ----

export default function AdminAMLPage() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('flags');
  const [amlFlags, setAmlFlags] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolveDialog, setResolveDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [resolveForm, setResolveForm] = useState({ resolution: 'resolved', resolver_name: '', resolved_date: '', resolution_notes: '' });
  const [uploading, setUploading] = useState({ front: false, back: false });
  const [idImages, setIdImages] = useState({ front: null, back: null });
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [flagsRes, statsRes] = await Promise.all([api.getAdminAML(), api.getAdminAMLStats()]);
      setAmlFlags(flagsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch AML data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFlags = amlFlags.filter(flag => {
    if (statusFilter !== 'all' && flag.status !== statusFilter) return false;
    if (riskFilter !== 'all' && flag.risk_level !== riskFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (flag.user_name || '').toLowerCase().includes(q)
        || (flag.user_email || '').toLowerCase().includes(q)
        || (flag.flag_type || '').toLowerCase().includes(q)
        || (flag.description || '').toLowerCase().includes(q);
    }
    return true;
  });

  const openResolveDialog = (flag) => {
    setSelectedFlag(flag);
    setResolveForm({ resolution: 'resolved', resolver_name: '', resolved_date: new Date().toISOString().slice(0, 10), resolution_notes: '' });
    setIdImages({ front: null, back: null });
    setResolveDialog(true);
  };

  const openViewDialog = (flag) => { setSelectedFlag(flag); setViewDialog(true); };

  const handleIdUpload = async (side, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('aml_image_only')); return; }
    setUploading(prev => ({ ...prev, [side]: true }));
    try {
      const res = await api.uploadAMLId(selectedFlag.id, side, file);
      setIdImages(prev => ({ ...prev, [side]: res.data.file_url }));
      toast.success(t('aml_upload_success').replace('{side}', side === 'front' ? t('aml_id_front') : t('aml_id_back')));
    } catch { toast.error(t('aml_upload_failed')); }
    finally { setUploading(prev => ({ ...prev, [side]: false })); }
  };

  const handleResolve = async () => {
    if (!resolveForm.resolver_name.trim()) { toast.error(t('aml_name_required')); return; }
    if (!resolveForm.resolved_date) { toast.error(t('aml_date_required')); return; }
    try {
      await api.resolveAMLFlag(selectedFlag.id, resolveForm);
      toast.success(t('admin_aml_resolved'));
      setResolveDialog(false);
      fetchData();
    } catch { toast.error(t('aml_resolve_failed')); }
  };

  const handleStatusChange = async (flagId, newStatus) => {
    try {
      await api.updateAMLFlagStatus(flagId, newStatus);
      toast.success(`${newStatus}`);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (flagId) => {
    if (!window.confirm(t('admin_aml_delete_confirm'))) return;
    try {
      await api.deleteAMLFlag(flagId);
      toast.success(t('admin_aml_deleted'));
      fetchData();
    } catch { toast.error(t('aml_delete_failed')); }
  };

  const getRiskBadge = (level) => {
    const config = {
      low: { label: t('admin_aml_risk_low'), cls: 'bg-blue-500/15 text-blue-700 border-blue-500/20' },
      medium: { label: t('admin_aml_risk_medium'), cls: 'bg-amber-500/15 text-amber-700 border-amber-500/20' },
      high: { label: t('admin_aml_risk_high'), cls: 'bg-red-500/15 text-red-700 border-red-500/20' },
    }[level] || { label: level, cls: 'bg-gray-500/15 text-gray-700 border-gray-500/20' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.cls}`}>{t('aml_risk_prefix')}: {config.label}</span>;
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { label: t('admin_aml_status_open'), cls: 'bg-red-500/15 text-red-700 border-red-500/20' },
      investigating: { label: t('admin_aml_status_investigating'), cls: 'bg-blue-500/15 text-blue-700 border-blue-500/20' },
      resolved: { label: t('admin_aml_status_resolved'), cls: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/20' },
      false_positive: { label: t('admin_aml_status_false_positive'), cls: 'bg-gray-500/15 text-gray-700 border-gray-500/20' },
    }[status] || { label: status, cls: 'bg-gray-500/15 text-gray-700 border-gray-500/20' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.cls}`}>{config.label}</span>;
  };

  const getFlagTypeLabel = (type) => {
    const key = `aml_flag_${type}`;
    const translated = t(key);
    return translated !== key ? translated : type;
  };

  const tabs = [
    { id: 'flags', label: t('aml_tab_flags'), icon: Shield },
    { id: 'stats', label: t('aml_tab_stats'), icon: BarChart3 },
    { id: 'users', label: t('aml_tab_users'), icon: Users },
    { id: 'settings', label: t('aml_tab_settings'), icon: Settings },
  ];

  const IdImageView = ({ url, label }) => (
    url ? (
      <div className="space-y-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="border border-border rounded-lg overflow-hidden bg-white">
          <img src={`${API_URL}${url}`} alt={label} className="w-full h-40 object-contain" />
        </div>
      </div>
    ) : null
  );

  return (
    <AdminLayout>
      <div data-testid="admin-aml-page">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">{t('admin_nav_aml')}</h1>
          <p className="text-muted-foreground">{t('aml_subtitle')}</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-secondary/30 rounded-xl p-1" data-testid="aml-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground hover:text-primary'}`}
              data-testid={`aml-tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'stats' && <StatsTab stats={stats} t={t} />}
        {activeTab === 'settings' && <SettingsTab t={t} />}
        {activeTab === 'users' && <RiskUsersTab t={t} />}
        {activeTab === 'flags' && (
          <div>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="aml-filters">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('admin_aml_search')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="aml-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]" data-testid="aml-status-filter">
                  <ArrowUpDown className="h-3.5 w-3.5 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('aml_filter_all')}</SelectItem>
                  <SelectItem value="open">{t('aml_filter_open')}</SelectItem>
                  <SelectItem value="investigating">{t('aml_filter_investigating')}</SelectItem>
                  <SelectItem value="resolved">{t('aml_filter_resolved')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={riskFilter} onValueChange={setRiskFilter}>
                <SelectTrigger className="w-[160px]" data-testid="aml-risk-filter">
                  <Shield className="h-3.5 w-3.5 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('aml_filter_all')}</SelectItem>
                  <SelectItem value="high">{t('aml_filter_high')}</SelectItem>
                  <SelectItem value="medium">{t('aml_filter_medium')}</SelectItem>
                  <SelectItem value="low">{t('aml_filter_low')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Flags List */}
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
            ) : filteredFlags.length === 0 ? (
              <div className="text-center py-20">
                <Shield className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">{t('admin_data_not_found')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFlags.map(flag => (
                  <div key={flag.id} className="bg-card border border-border/50 rounded-2xl p-6" data-testid={`aml-flag-${flag.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="font-heading text-lg font-semibold text-primary">{getFlagTypeLabel(flag.flag_type)}</h3>
                          {getRiskBadge(flag.risk_level)}
                          {getStatusBadge(flag.status)}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{flag.description}</p>
                        {/* User info row */}
                        {flag.user_name && (
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-1">
                            <span>{t('aml_user_name')}: <strong className="text-foreground">{flag.user_name}</strong></span>
                            <span>{flag.user_email}</span>
                            {flag.user_risk_score > 0 && (
                              <span className="font-medium">
                                {t('aml_user_risk_score')}: <strong className={flag.user_risk_score >= 70 ? 'text-red-600' : flag.user_risk_score >= 40 ? 'text-amber-600' : 'text-blue-600'}>{flag.user_risk_score}</strong>/100
                              </span>
                            )}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {t('aml_created_at')}: {new Date(flag.created_at).toLocaleString('ja-JP')}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0 ml-4">
                        {flag.status === 'open' && (
                          <Button size="sm" variant="outline" onClick={() => handleStatusChange(flag.id, 'investigating')} data-testid={`investigate-aml-${flag.id}`}>
                            <Search className="h-4 w-4 mr-1" />{t('aml_set_investigating')}
                          </Button>
                        )}
                        {(flag.status === 'resolved' || flag.status === 'false_positive') && (
                          <Button size="sm" variant="outline" onClick={() => openViewDialog(flag)} data-testid={`view-aml-${flag.id}`}>
                            <Eye className="h-4 w-4 mr-1" />{t('aml_details')}
                          </Button>
                        )}
                        {(flag.status === 'open' || flag.status === 'investigating') && (
                          <Button size="sm" onClick={() => openResolveDialog(flag)} data-testid={`resolve-aml-${flag.id}`}>
                            <CheckCircle className="h-4 w-4 mr-1" />{t('aml_resolve_record')}
                          </Button>
                        )}
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(flag.id)} data-testid={`delete-aml-${flag.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {(flag.status === 'resolved' || flag.status === 'false_positive') && flag.resolver_name && (
                      <div className="border-t border-border pt-4 mt-2">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="text-muted-foreground text-xs mb-0.5">{t('aml_resolver_label')}</div>
                            <div className="font-medium">{flag.resolver_name}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground text-xs mb-0.5">{t('aml_resolved_date_label')}</div>
                            <div>{flag.resolved_date || '-'}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground text-xs mb-0.5">{t('aml_notes_label')}</div>
                            <div className="truncate">{flag.resolution_notes || '-'}</div>
                          </div>
                        </div>
                        {(flag.id_front_image || flag.id_back_image) && (
                          <div className="flex items-center gap-2 mt-2 text-xs text-emerald-600">
                            <Image className="h-3.5 w-3.5" />{t('aml_id_attached')}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 text-sm pt-4 border-t border-border mt-4">
                      <div>
                        <div className="text-muted-foreground mb-1">{t('aml_user_id')}</div>
                        <div className="font-mono text-xs">{flag.user_id}</div>
                      </div>
                      {flag.transfer_id && (
                        <div>
                          <div className="text-muted-foreground mb-1">{t('print_col_id')}</div>
                          <div className="font-mono text-xs">{flag.transfer_id}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Resolve Dialog */}
        <Dialog open={resolveDialog} onOpenChange={setResolveDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('aml_resolve_title')}</DialogTitle>
              <DialogDescription>{t('aml_flag_label')}: {selectedFlag?.flag_type}</DialogDescription>
            </DialogHeader>
            {selectedFlag && (
              <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
                <div className="text-sm text-muted-foreground bg-secondary/30 rounded-lg p-3">{selectedFlag.description}</div>
                <div className="space-y-2">
                  <Label>{t('aml_resolve_type')}</Label>
                  <Select value={resolveForm.resolution} onValueChange={v => setResolveForm(p => ({ ...p, resolution: v }))} data-testid="resolve-type-select">
                    <SelectTrigger data-testid="resolve-type-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="resolved">{t('admin_aml_status_resolved')}</SelectItem>
                      <SelectItem value="false_positive">{t('admin_aml_status_false_positive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('aml_resolver_name')} <span className="text-red-500">*</span></Label>
                    <Input value={resolveForm.resolver_name} onChange={e => setResolveForm(p => ({ ...p, resolver_name: e.target.value }))} placeholder={t('aml_name_placeholder')} data-testid="resolver-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('aml_resolved_date')} <span className="text-red-500">*</span></Label>
                    <Input type="date" value={resolveForm.resolved_date} onChange={e => setResolveForm(p => ({ ...p, resolved_date: e.target.value }))} data-testid="resolved-date-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('aml_resolution_notes')}</Label>
                  <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" value={resolveForm.resolution_notes} onChange={e => setResolveForm(p => ({ ...p, resolution_notes: e.target.value }))} placeholder={t('aml_resolution_notes_placeholder')} data-testid="resolution-notes-input" />
                </div>
                <div className="border border-border rounded-xl p-4 space-y-3">
                  <div className="text-sm font-semibold">{t('aml_id_upload')}</div>
                  <div className="grid grid-cols-2 gap-4">
                    <CardGuardUpload label={t('aml_id_front')} previewUrl={idImages.front ? `${API_URL}${idImages.front}` : null} uploading={uploading.front} onUpload={file => handleIdUpload('front', { target: { files: [file] } })} onRemove={() => setIdImages(p => ({ ...p, front: null }))} testId="id-front-upload" />
                    <CardGuardUpload label={t('aml_id_back')} previewUrl={idImages.back ? `${API_URL}${idImages.back}` : null} onUpload={file => handleIdUpload('back', { target: { files: [file] } })} onRemove={() => setIdImages(p => ({ ...p, back: null }))} uploading={uploading.back} testId="id-back-upload" />
                  </div>
                </div>
                <Button className="w-full" onClick={handleResolve} data-testid="submit-resolve-button">{t('aml_save_resolve')}</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View Detail Dialog */}
        <Dialog open={viewDialog} onOpenChange={setViewDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('aml_resolve_detail_title')}</DialogTitle>
            </DialogHeader>
            {selectedFlag && (
              <div className="space-y-5 mt-2 max-h-[70vh] overflow-y-auto pr-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-heading text-lg font-semibold">{getFlagTypeLabel(selectedFlag.flag_type)}</h3>
                  {getRiskBadge(selectedFlag.risk_level)}
                  {getStatusBadge(selectedFlag.status)}
                </div>
                <div className="text-sm text-muted-foreground bg-secondary/30 rounded-lg p-3">{selectedFlag.description}</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground text-xs mb-0.5">{t('aml_user_id')}</div>
                    <div className="font-mono text-xs">{selectedFlag.user_id}</div>
                  </div>
                  {selectedFlag.transfer_id && (
                    <div>
                      <div className="text-muted-foreground text-xs mb-0.5">{t('print_col_id')}</div>
                      <div className="font-mono text-xs">{selectedFlag.transfer_id}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-muted-foreground text-xs mb-0.5">{t('aml_created_at')}</div>
                    <div>{new Date(selectedFlag.created_at).toLocaleString('ja-JP')}</div>
                  </div>
                  {selectedFlag.resolved_at && (
                    <div>
                      <div className="text-muted-foreground text-xs mb-0.5">{t('aml_resolved_at')}</div>
                      <div>{new Date(selectedFlag.resolved_at).toLocaleString('ja-JP')}</div>
                    </div>
                  )}
                </div>
                <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-xl p-4 space-y-3" data-testid="view-resolution-record">
                  <div className="text-sm font-semibold text-emerald-700">{t('aml_resolve_record')}</div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground text-xs mb-0.5">{t('aml_resolver_name')}</div>
                      <div className="font-medium" data-testid="view-resolver-name">{selectedFlag.resolver_name || <span className="text-muted-foreground/50">{t('not_entered')}</span>}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-xs mb-0.5">{t('aml_resolved_date')}</div>
                      <div data-testid="view-resolved-date">{selectedFlag.resolved_date || <span className="text-muted-foreground/50">{t('not_entered')}</span>}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-0.5">{t('aml_resolution_notes')}</div>
                    <div className="text-sm whitespace-pre-wrap bg-white/50 rounded-lg p-3 border border-border/50" data-testid="view-resolution-notes">{selectedFlag.resolution_notes || <span className="text-muted-foreground/50">{t('no_records')}</span>}</div>
                  </div>
                </div>
                {(selectedFlag.id_front_image || selectedFlag.id_back_image) && (
                  <div className="border border-border rounded-xl p-4 space-y-3" data-testid="view-id-images">
                    <div className="text-sm font-semibold">{t('aml_id_doc')}</div>
                    <div className="grid grid-cols-2 gap-4">
                      <IdImageView url={selectedFlag.id_front_image} label={t('aml_id_front')} />
                      <IdImageView url={selectedFlag.id_back_image} label={t('aml_id_back')} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
